import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { identifyUser, resetMixpanel, track } from '../services/analytics';
import { setSentryUser } from '../sentry';
import { clearAllSwr } from '../services/prefetch';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  function fetchUser() {
    return axios.get(`${API}/auth/me?token=${token}`)
      .then((response) => {
        setUser(response.data);
        // Re-identify on page refresh so session continues tracking
        identifyUser(response.data);
        setSentryUser(response.data);
      })
      .catch((error) => {
        console.error('Error fetching user:', error);
        logout();
      })
      .finally(() => setLoading(false));
  }

  function logout() {
    track('manager_logout');
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    resetMixpanel();
    setSentryUser(null);
    // Drop persisted SWR responses so the next user doesn't see this user's
    // tenant list / KPIs on cold boot.
    clearAllSwr();
  }

  useEffect(() => {
    // Check if user is logged in on mount
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (phone, otp) => {
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        phone,
        otp
      });
      
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Mixpanel: identify + track login
      identifyUser(userData);
      setSentryUser(userData);
      track('manager_login_succeeded', { role: userData.role });

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Invalid OTP' 
      };
    }
  };

  const sendOTP = async (phone) => {
    try {
      const response = await axios.post(`${API}/auth/send-otp`, { phone });
      track('otp_requested', { phone_last4: phone.slice(-4) });
      return { 
        success: true, 
        otp: response.data.otp // For demo purposes only
      };
    } catch (error) {
      track('otp_request_failed', { reason: error.response?.data?.detail?.slice(0, 100) });
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to send OTP' 
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    sendOTP,
    isAuthenticated: !!token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
